import { UniAbcState } from "@/store/uniabc/state";
import { State as RootState } from "@/store/state";
import { ActionContext } from "vuex";
import {
  commitDeleteEntry,
  commitDeleteReference,
  commitLastEdit,
  commitSetApproval,
  commitSetEntries,
  commitSetEntry,
  commitSetEntryImageUrl,
  commitSetFLCID,
  commitSetLastLoad,
  commitSetReference
} from "@/store/uniabc/mutations";
import Entry from "@/interfaces/uniabc/Entry";
import { getStoreAccessors } from "typesafe-vuex";
import { api } from "@/api";
import Reference from "@/interfaces/uniabc/Reference";
import {
  dispatchRaiseErrorTimeout,
  dispatchResetUnsavedChangesState
} from "@/store/appState/actions";
import store from "@/store";
import { commitHasUnsavedChanges } from "@/store/appState/mutations";

type UniAbcContext = ActionContext<UniAbcState, RootState>;

export const actions = {
  async addEntry(context: UniAbcContext, label: string) {
    const entry = {
      label: label,
      // eslint-disable-next-line @typescript-eslint/camelcase
      term_de: "",
      // eslint-disable-next-line @typescript-eslint/camelcase
      alt_term_de: "",
      // eslint-disable-next-line @typescript-eslint/camelcase
      term_en: "",
      // eslint-disable-next-line @typescript-eslint/camelcase
      alt_term_en: "",
      // eslint-disable-next-line @typescript-eslint/camelcase
      text_de: "",
      // eslint-disable-next-line @typescript-eslint/camelcase
      text_en: "",
      // eslint-disable-next-line @typescript-eslint/camelcase
      general_comments: "",
      // eslint-disable-next-line @typescript-eslint/camelcase
      refs: [],
      comments: "",
      section: "",
      // eslint-disable-next-line @typescript-eslint/camelcase
      image_url: "",
      lastApproved: { date: new Date(0), name: "" },
      lastEdit: new Date()
    };

    commitSetEntry(context, entry);
    await context.dispatch("unsavedChangesNotify", null, { root: true });
  },

  async updateEntry(context: UniAbcContext, entry: Entry) {
    commitSetEntry(context, entry);
    commitLastEdit(context, entry.label);
    await context.dispatch("unsavedChangesNotify", null, { root: true });
  },

  async deleteEntry(context: UniAbcContext, entry: Entry) {
    await commitDeleteEntry(context, entry);
    await context.dispatch("unsavedChangesNotify", null, { root: true });
  },

  async loadEntries(context: UniAbcContext) {
    const gitLab = context.rootGetters.getGitLabInfo;

    try {
      const response = await api.getAbcJson(gitLab.projectId, gitLab.branch);
      const respMeta = await api.getAbcJsonMeta(
        gitLab.projectId,
        gitLab.branch
      );

      commitSetEntries(context, response.data);
      commitSetLastLoad(context, new Date());
      commitSetFLCID(context, respMeta.data.last_commit_id);
      await context.dispatch("resetUnsavedChangesState", null, { root: true });
    } catch (err) {
      await dispatchRaiseErrorTimeout(
        store,
        new Error(`Error while fetching metadata of UniABC-JSON. <br/> ${err}`)
      );
    }
  },

  async setImageUrlOfEntry(
    context: UniAbcContext,
    payload: {
      label: string;
      imageUrl: string;
    }
  ) {
    commitSetEntryImageUrl(context, payload);
    commitLastEdit(context, payload.label);
    await context.dispatch("unsavedChangesNotify", null, { root: true });
  },
  async updateReference(
    context: UniAbcContext,
    payload: {
      label: string;
      ref: Reference;
    }
  ) {
    commitSetReference(context, payload);
    commitLastEdit(context, payload.label);
    await context.dispatch("unsavedChangesNotify", null, { root: true });
  },
  async deleteReference(
    context: UniAbcContext,
    payload: {
      label: string;
      ref: Reference;
    }
  ) {
    commitDeleteReference(context, {
      label: payload.label,
      ref: payload.ref
    });
    await context.dispatch("unsavedChangesNotify", null, { root: true });
  },
  async setSelfApprovalNow(context: UniAbcContext, payload: string) {
    commitSetApproval(context, {
      label: payload,
      approval: {
        name: context.rootGetters.getGitLabInfo.authorName,
        date: new Date()
      }
    });
    await context.dispatch("unsavedChangesNotify", null, { root: true });
  }
};

const { dispatch } = getStoreAccessors<UniAbcState, RootState>("");

export const dispatchUpdateEntry = dispatch(actions.updateEntry);
export const dispatchLoadEntries = dispatch(actions.loadEntries);
export const dispatchDeleteReference = dispatch(actions.deleteReference);
export const dispatchAddEntry = dispatch(actions.addEntry);
export const dispatchSetEntryImageUrl = dispatch(actions.setImageUrlOfEntry);
export const dispatchUpdateReference = dispatch(actions.updateReference);
export const dispatchDeleteEntry = dispatch(actions.deleteEntry);
export const dispatchSelfApprovalNow = dispatch(actions.setSelfApprovalNow);
